import React, { useState, useEffect, useMemo } from 'react';
import { Box, Flex, Heading, Text } from 'rebass';
import {
  Route, Switch, useParams, useHistory, useRouteMatch, useLocation,
} from 'react-router-dom';
import { FiLink, FiEdit2, FiChevronRight } from 'react-icons/fi';
import { HiOutlineUsers } from 'react-icons/hi';
import { MdOutlineMenuBook } from 'react-icons/md';
import { useWindowSize } from 'react-use';
import store from 'store';
import { IoIosArrowBack } from 'react-icons/io';

import useEntity from 'entities/hooks/use-entity';
import { useTalkingFamilyTreeSWR } from './api';
import Ftue, { FTUE_STORAGE_KEY } from './Ftue';
import { parseTalkingFamilyTree, pickInviteUrl, pickTftUrl } from './helpers';
import { FT_COLORS, FT_CONTENT_WIDTH } from './theme';
import { PrimaryButton, OutlineButton, LavenderButton } from './components/FtButton';
import FamilyMembersList from './FamilyMembersList';
import PagePreview from './PagePreview';
import BottomTabs from './components/BottomTabs';
import MemberFormModal from './MemberFormModal';
import DeleteMemberModal from './DeleteMemberModal';
import InvitationWizard from './invitation/InvitationWizard';
import PaymentBlocker from 'collections/milestones/PaymentBlocker';

// Success / alert banner — the design reserves the space above the header for these.
const SuccessBanner = ({ message }) =>
(message ? (
  <Flex
    alignItems="center"
    px={2}
    py={1}
    sx={{
      gap: '6px',
      borderRadius: '100px',
      bg: FT_COLORS.successBg,
      color: FT_COLORS.successText,
    }}
  >
    <HiOutlineUsers size={16} />
    <Text fontFamily="Poppins" fontSize="13px" fontWeight={500}>
      {message}
    </Text>
  </Flex>
) : null);

// Desktop-only top bar: current tab label (left), success banner (center),
// switch-tab pill (right). Replaces the mobile bottom bar on wide screens.
const TopTabBar = ({ baseRoute, active, search, successMessage }) => {
  const { replace } = useHistory();
  const isPreview = active === 'preview';
  return (
    <Flex
      alignItems="center"
      justifyContent="space-between"
      px={4}
      py={3}
      sx={{ borderBottom: '1px solid', borderColor: FT_COLORS.rowBorder, display: ['none !important', 'none !important', 'flex !important'] }}
    >
      <Text fontFamily="Poppins" fontWeight={600} fontSize="18px" color={FT_COLORS.text}>
        {isPreview ? 'Page Preview' : 'Family'}
      </Text>
      <SuccessBanner message={successMessage} />
      <OutlineButton
        onClick={() => replace(`${baseRoute}${isPreview ? '' : '/preview'}${search}`)}
        style={{ height: '38px' }}
      >
        <Box as={isPreview ? HiOutlineUsers : MdOutlineMenuBook} size={24} minWidth={24} />
        {isPreview ? 'Family' : 'Page Preview'}
      </OutlineButton>
    </Flex>
  );
};

// Per-baby Talking Family Tree page. Mounted at /:type/:id/family-tree from
// collections/index.js — renders full-width beside the desktop sidebar, and inside
// the mobile drawer on small screens.
const FamilyTree = () => {
  const { type, id } = useParams();
  const { push, replace, goBack } = useHistory();
  const { search } = useLocation();
  const baseRoute = `/${type}/${id}/family-tree`;

  const previewMatch = useRouteMatch(`${baseRoute}/preview`);
  const activeTab = previewMatch ? 'preview' : 'family';

  // On desktop the invitation flow is a full page (not a modal), so the family-tree
  // chrome (top bar / header / bottom tabs) is hidden while it's open.
  const { width } = useWindowSize();
  const isDesktop = width >= 768;
  const inviteMatch = useRouteMatch(`${baseRoute}/invite`);
  const hideChrome = !!inviteMatch && isDesktop;
  const [totalMembers, setTotalMember] = useState(0);
  const [showPaymentPage, setShowPaymentPage] = useState(null);

  const { entity: journal } = useEntity({ type: 'child-journals', id });
  const babyName = journal?.['subject-name'] || journal?.name || 'your baby';
  const isOnFreeTrial = journal?.['child-journal-talking-family-tree-permitted'];

  const { data: tftData } = useTalkingFamilyTreeSWR(id);
  const tft = tftData?.data?.data?.attributes || {};
  const parsed = useMemo(() => parseTalkingFamilyTree(tftData), [tftData]);

  const inviteUrl = pickInviteUrl(tft);

  const tftUrl = pickTftUrl(tft);

  useEffect(() => {
    setShowPaymentPage(isOnFreeTrial)
  }, [isOnFreeTrial]);

  // Transient success banner driven by ?added=… (set after add/edit/delete).
  const params = new URLSearchParams(search);
  const added = params.get('added');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (!added) return undefined;
    setSuccessMessage(added === 'removed' ? 'Family member removed' : 'Family added!');
    const t = setTimeout(() => {
      setSuccessMessage('');
      replace(baseRoute);
    }, 3000);
    return () => clearTimeout(t);
  }, [added, baseRoute, replace]);

  // First-time-user experience — shown once (persisted in localStorage).
  const [showFtue, setShowFtue] = useState(() => !store.get(FTUE_STORAGE_KEY));
  const dismissFtue = () => {
    store.set(FTUE_STORAGE_KEY, true);
    setShowFtue(false);
  };

  const [copied, setCopied] = useState(false);
  const onCopyInvite = () => {
    if (!inviteUrl) {
      push(`${baseRoute}/invite`);
      return;
    }
    try {
      navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      push(`${baseRoute}/invite`);
    }
  };

  if (showPaymentPage === false)
    return <Box m={width < 600 ? 2 : 4} mr={2} >
      <Box sx={width < 600 && { bg: '#fff', position: 'fixed', bottom: 0, left: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }} p={2}>
        <Flex
          height={40}
          width="min-content"
          justifyContent="space-around"
          alignItems="center"
          sx={{ borderRadius: 'medium', cursor: 'pointer' }}
          onClick={goBack}
        >
          <Text as={IoIosArrowBack} color="mineShaft" size={24} />
          <Text minWidth={'max-content'} color="mineShaft" fontFamily="Poppins" lineHeight={'150%'} ml={1} fontWeight={400} fontSize="15px">
            Back
          </Text>
        </Flex>
        <svg xmlns="http://www.w3.org/2000/svg" width="70vw" height="2" viewBox="0 0 70vw 2" fill="none">
          <path d="M0 1L1027 1.00009" stroke="#E2E8F0" stroke-width="0.5" />
        </svg>
      </Box>
      <PaymentBlocker sku="by-feature-talkingfamilytree" />
    </Box>

  return (
    <Flex flexDirection="column" css="min-height: 100vh;">
      {!hideChrome && (
        <>
          <TopTabBar baseRoute={baseRoute} active={activeTab} search={search} successMessage={successMessage} />

          <Box flex="1 1 auto" maxWidth={FT_CONTENT_WIDTH} width="100%" mx="auto" px={[3, 3, 4]} pt={[3, 4]}>
            {/* Mobile success banner (desktop shows it in the top bar) */}
            {successMessage && (
              <Flex display={['flex', 'flex', 'none']} justifyContent="center" mb={2}>
                <SuccessBanner message={successMessage} />
              </Flex>
            )}



            {/* Tab content */}
            <Switch>
              <Route path={`${baseRoute}/preview`} render={() => <PagePreview journalId={id} tftUrl={tftUrl} />} />
              <Route path={baseRoute} render={() => <>
                {/* Header */}
                <Flex flexDirection="column" alignItems="center" textAlign="center" mb={3}>
                  <Heading fontFamily="Bitter" fontWeight={500} fontSize={['26px', '28px']} color={FT_COLORS.text}>
                    Talking Family Tree
                  </Heading>
                  <Text fontFamily="Poppins" fontSize="14px" color="#64748B" mt={2} maxWidth={420}>
                    {`Invite family members to create a welcome video in ${babyName}'s family tree.`}
                  </Text>

                  <Box width="100%" mt={3}>
                    {(inviteUrl && parsed?.selectedTopicIds?.length > 0) ? (
                      <Flex sx={{ gap: '10px' }} justifyContent="center" >
                        <PrimaryButton onClick={onCopyInvite}>
                          <FiLink size={20} />
                          {copied ? 'Copied!' : 'Copy invite link'}
                        </PrimaryButton>
                        <OutlineButton onClick={() => push(`${baseRoute}/invite`)}>
                          <FiEdit2 size={20} />
                          Edit invite
                        </OutlineButton>
                      </Flex>
                    ) : (
                      <LavenderButton onClick={() => push(`${baseRoute}/invite`)}>
                        <Flex alignItems="center" sx={{ gap: '8px' }}>
                          <FiLink />
                          Create an invitation
                        </Flex>
                        <FiChevronRight />
                      </LavenderButton>
                    )}
                  </Box>
                </Flex>
                <FamilyMembersList journalId={id} baseRoute={baseRoute} setTotalMember={setTotalMember} />
              </>
              } />
            </Switch>
          </Box>

          {/* Mobile bottom tabs (hidden on desktop) */}
          <Box display={['block', 'block', 'none']} css="position: sticky; bottom: 0;">
            <BottomTabs baseRoute={baseRoute} active={activeTab} />
          </Box>
        </>
      )}

      {/* Overlay routes (modals / wizard) */}
      <Route
        exact
        path={`${baseRoute}/members/new`}
        render={() => <MemberFormModal journalId={id} babyName={babyName} baseRoute={baseRoute} />}
      />
      <Route
        exact
        path={`${baseRoute}/members/:memberId/edit`}
        render={() => <MemberFormModal journalId={id} babyName={babyName} baseRoute={baseRoute} />}
      />
      <Route
        exact
        path={`${baseRoute}/members/:memberId/delete`}
        render={() => <DeleteMemberModal journalId={id} baseRoute={baseRoute} />}
      />
      <Route
        path={`${baseRoute}/invite`}
        render={() => (
          <InvitationWizard journalId={id} babyName={babyName} baseRoute={baseRoute} isDesktop={isDesktop} />
        )}
      />

      {showFtue && (
        <Ftue onClose={dismissFtue} />
      )}
    </Flex>
  );
};

export default FamilyTree;
